import { useQueryClient } from '@tanstack/react-query';

import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { studentAssessmentQueries } from '@/data/query-keys/studentAssessmentQueryKey';
import { StudentAssessmentTypeEnum } from '@/models/StudentAssessment';
import { toStudentPedagogicalSheetRecords } from '@/utils/assessments/studentPedagogicalSheetRecordAdapters';
import { DateRangeFilterParams } from '@/utils/filters/dateRangeParams';

const RECORDS_PAGE_SIZE = 10;

const ALL_ASSESSMENT_TYPES = [
  StudentAssessmentTypeEnum.OCCURRENCE,
  StudentAssessmentTypeEnum.BEHAVIOR,
];

type UseStudentPedagogicalSheetRecordsParams = {
  studentId: number;
  filters?: DateRangeFilterParams;
  scheduledLessonId?: number;
  types?: StudentAssessmentTypeEnum[];
  enabled?: boolean;
};

export default function useStudentPedagogicalSheetRecords({
  studentId,
  filters = {},
  scheduledLessonId,
  types = ALL_ASSESSMENT_TYPES,
  enabled = true,
}: UseStudentPedagogicalSheetRecordsParams) {
  const { dateRangeAfter, dateRangeBefore } = filters;

  const queryClient = useQueryClient();

  const {
    results,
    isInitialLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...studentAssessmentQueries.infinite({
      studentId,
      scheduledLessonId: scheduledLessonId ? [scheduledLessonId] : undefined,
      type: types,
      dateRangeAfter,
      dateRangeBefore,
      ordering: 'created_at',
      pageSize: RECORDS_PAGE_SIZE,
    }),
    keepPreviousData: true,
    staleTime,
    enabled: enabled && studentId > 0,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: studentAssessmentQueries.all });

  return {
    records: toStudentPedagogicalSheetRecords(results),
    isLoading: isInitialLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    invalidate,
  };
}
